/**
 * Format a reviewer's display name for public display as their
 * first name only — keeps reviews lightly anonymous while still
 * feeling personal.
 *
 *   "John Reeves"   -> "John"
 *   "Matt Pryse"    -> "Matt"
 *   "Madonna"       -> "Madonna"      (single name, kept as-is)
 *   "8ptcfwrysy"    -> "A camper"     (opaque OAuth handle)
 *   "" / undefined  -> "A camper"
 *
 * Used for review author bylines and community-photo attribution
 * overlays so the whole site shows the same shortened credit.
 */

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/**
 * Heuristic: an opaque OAuth/provider handle (e.g. "8ptcfwrysy",
 * "u8f3k9d2") rather than a real name — lowercase alphanumeric,
 * contains a digit, no spaces. We don't want to surface these.
 */
const looksLikeOpaqueHandle = (token: string): boolean =>
  /\d/.test(token) && /^[a-z0-9]+$/.test(token) && token.length >= 6;

export function formatReviewerName(name?: string | null): string {
  const raw = (name || '').trim();
  if (!raw) return 'A camper';

  // First whitespace-separated token only.
  const first = raw.split(/\s+/)[0];
  if (looksLikeOpaqueHandle(first)) return 'A camper';
  return capitalize(first);
}
